import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

const schema = yup.object({
  nameEn: yup
    .string()
    .trim()
    .required('English ingredient is required')
    .max(100, 'English ingredient should be at most 100 characters'),
  nameNo: yup
    .string()
    .trim()
    .required('Norwegian ingredient is required')
    .max(100, 'Norwegian ingredient should be at most 100 characters'),
});

const getDefaultValues = (ingredient) => ({
  nameEn: ingredient?.nameEn ?? '',
  nameNo: ingredient?.nameNo ?? '',
});

const IngredientManagementDialog = ({
  open,
  onClose,
  onSubmit,
  ingredient,
  isSubmitting,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(ingredient),
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(ingredient));
    }
  }, [ingredient, open, reset]);

  const submitForm = (data) => {
    onSubmit({
      nameEn: data.nameEn.trim(),
      nameNo: data.nameNo.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth='md'
    >
      <DialogContent sx={{ p: 0 }}>
        <Stack px={4} py={3} rowGap={3}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='s20_w500' component='span'>
              <strong>Ingredient Management:</strong>{' '}
              {ingredient ? 'Edit ingredient' : 'Add a new ingredient'}
            </Typography>
            <IconButton onClick={onClose} disabled={isSubmitting}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <form onSubmit={handleSubmit(submitForm)}>
            <Stack rowGap={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                <Box flex={1}>
                  <Stack direction='row' gap={1} alignItems='center' mb={1}>
                    <Chip
                      label='EN'
                      size='small'
                      sx={{
                        backgroundColor: '#EEF4FF',
                        color: '#1A3A7A',
                        fontWeight: 700,
                      }}
                    />
                    <Typography variant='s16_w600'>Add English Ingredient</Typography>
                  </Stack>
                  <Controller
                    name='nameEn'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder='Ingredient Name'
                        error={!!errors.nameEn}
                        helperText={errors.nameEn?.message}
                      />
                    )}
                  />
                </Box>

                <Box flex={1}>
                  <Stack direction='row' gap={1} alignItems='center' mb={1}>
                    <Chip
                      label='NW'
                      size='small'
                      sx={{
                        backgroundColor: '#FFF3E8',
                        color: '#C25A1B',
                        fontWeight: 700,
                      }}
                    />
                    <Typography variant='s16_w600'>Add Norwegian Ingredient</Typography>
                  </Stack>
                  <Controller
                    name='nameNo'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder='Ingredient Name'
                        error={!!errors.nameNo}
                        helperText={errors.nameNo?.message}
                      />
                    )}
                  />
                </Box>
              </Stack>

              <Stack direction='row' gap={2} justifyContent='flex-start'>
                <Button variant='contained' type='submit' disabled={isSubmitting}>
                  Save
                </Button>
                <Button variant='outlined' onClick={onClose} disabled={isSubmitting}>
                  Discard
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default IngredientManagementDialog;
